import { Container, Row, Col } from 'react-bootstrap';
import Link from 'next/link';
import { BsAt, BsTelephone, BsClock, BsPinMap, BsHouseDoor, BsShieldCheck } from 'react-icons/bs';
import { useSiteGlobals } from '@/states';
import { useRegionsList } from '@/hooks';
import { toImageSrc, toPathSegment } from '@/utils/imageUrl';
import ReviewWidgets from '@/views/excursions/Detail/components/ReviewWidgets';
import footerBrandIcon from '@/assets/images/logo-main.png';

const Footer = ({ footerEmbedScripts = null }) => {
  const { footer } = useSiteGlobals();
  const { data: regionsData } = useRegionsList();
  const rawCompanyName = footer?.company_name?.trim();
  const companyName =
    !rawCompanyName || /^South Crete Tours$/i.test(rawCompanyName)
      ? 'South Crete Tours SA'
      : rawCompanyName;
  const copyrightText = footer?.copyright_text?.trim();
  const year = new Date().getFullYear();
  const supportEmail = 'info@southcrete.com';
  const supportPhone = '+30 28420 22892';
  const supportPhoneHref = supportPhone.replace(/\s+/g, '');
  const footerBrandIconSrc = toImageSrc(footerBrandIcon, '');
  const copyrightLine = copyrightText
    ? copyrightText.replace(/\d{4}/, String(year))
    : `© ${year} ${companyName}. All rights reserved.`;

  const reviewScriptsRaw = Array.isArray(footerEmbedScripts)
    ? footerEmbedScripts.map((s) => String(s || '').trim()).filter(Boolean).join('\n')
    : '';

  const serviceLinks = [
    { to: '/hotels', label: 'Hotels in Crete' },
    { to: '/excursions', label: 'Excursions & Tours' },
    { to: '/car-rental', label: 'Car Rental' },
    { to: null, label: 'Transfers' },
    { to: '/services', label: 'DMC Services' },
  ];

  const regionLinks = (Array.isArray(regionsData) ? regionsData : [])
    .filter(
      (region) =>
        (region?.is_active === 1 || region?.is_active === true) &&
        region?.name &&
        region?.slug
    )
    .sort((a, b) => {
      const aHotels = Number(a?.hotel_count ?? 0);
      const bHotels = Number(b?.hotel_count ?? 0);
      if (bHotels !== aHotels) {
        return bHotels - aHotels;
      }
      return Number(a?.order ?? 0) - Number(b?.order ?? 0);
    })
    .map((region) => {
      const safeSlug = toPathSegment(region.slug, '');
      return safeSlug
        ? { to: `/east-crete/${encodeURIComponent(safeSlug)}`, label: region.name }
        : null;
    })
    .filter(Boolean);

  const eastCreteGuideLinks = [{ to: '/east-crete', label: 'East Crete Guide' }, ...regionLinks];

  const companyLinks = [
    { to: '/about', label: 'About Us' },
    { to: '/become-a-partner', label: 'Become a Partner' },
    { to: '/contact', label: 'Contact' },
  ];

  const legalLinks = [
    { to: '/privacy-policy', label: 'Privacy Policy' },
    { to: '/cookie-policy', label: 'Cookie Policy' },
    { to: '/terms-of-use', label: 'Terms of Use' },
  ];

  return (
    <>
      <footer className="site-footer text-white">
        <Container>
          <Row className="g-4 g-xl-5">
            <Col lg={4}>
              <div className="site-footer__brand-row mb-3">
                {footerBrandIconSrc ? (
                  <img
                    src={footerBrandIconSrc}
                    alt={`${companyName} logo`}
                    className="site-footer__brand-icon"
                    loading="lazy"
                  />
                ) : null}
                <h2 className="site-footer__brand mb-0">{companyName}</h2>
              </div>
              <p className="site-footer__lead mb-3">
                Destination management and travel services in East Crete: handpicked hotels, excursions &amp; cultural
                tours car rentals and transfers services for tour operators, groups travelers and partners.
              </p>
              <ul className="list-unstyled site-footer__contact-list mb-0">
                <li>
                  <BsHouseDoor aria-hidden />
                  <span>Lasthenous 36 str, 72200</span>
                </li>
                <li>
                  <BsPinMap aria-hidden />
                  <span>Ierapetra, Crete, Greece</span>
                </li>
                <li>
                  <BsAt aria-hidden />
                  <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </li>
                <li>
                  <BsTelephone aria-hidden />
                  <a href={`tel:${supportPhoneHref}`}>{supportPhone}</a>
                </li>
                <li>
                  <BsClock aria-hidden />
                  <span>Mon - Sat: 09:00 - 20:00</span>
                </li>
              </ul>
            </Col>

            <Col xs={6} md={4} lg={2}>
              <h3 className="site-footer__title">Services</h3>
              <ul className="list-unstyled site-footer__link-list">
                {serviceLinks.map((item) => (
                  <li key={`${item.label}-${item.to || 'nolink'}`}>
                    {item.to ? <Link href={item.to}>{item.label}</Link> : <span>{item.label}</span>}
                  </li>
                ))}
              </ul>
            </Col>

            <Col xs={6} md={4} lg={2}>
              <h3 className="site-footer__title">Destinations</h3>
              <ul className="list-unstyled site-footer__link-list">
                {eastCreteGuideLinks.map((item) => (
                  <li key={item.to}>
                    <Link href={item.to}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </Col>

            <Col xs={6} md={4} lg={2}>
              <h3 className="site-footer__title">Company</h3>
              <ul className="list-unstyled site-footer__link-list">
                {companyLinks.map((item) => (
                  <li key={item.to}>
                    <Link href={item.to}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </Col>

            <Col xs={6} md={4} lg={2}>
              <h3 className="site-footer__title">Legal</h3>
              <ul className="list-unstyled site-footer__link-list">
                {legalLinks.map((item) => (
                  <li key={item.to}>
                    <Link href={item.to}>{item.label}</Link>
                  </li>
                ))}
                <li>
                  <a href="/sitemap.xml">Sitemap</a>
                </li>
                <li>
                  <a href="/robots.txt">Robots</a>
                </li>
              </ul>
            </Col>
          </Row>

          <hr className="site-footer__divider my-4" />
          <Row className="g-3 align-items-center">
            <Col md={8}>
              <p className="site-footer__copyright mb-0">{copyrightLine}</p>
            </Col>
            <Col md={4} className="text-md-end">
              <p className="site-footer__compliance mb-0">
                Local expertise. Reliable operations.
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
      <section className="site-footer-protection" aria-label="Security and anti-bot protection">
        <Container>
          <div className="site-footer-protection__inner">
            <span className="site-footer-protection__label">
              <BsShieldCheck aria-hidden />
              Protected by Cloudflare
            </span>
            <span className="site-footer-protection__text">
              Automated abuse protection is active with privacy-first bot checks.
            </span>
            <span className="site-footer-protection__links">
              <a href="https://www.cloudflare.com/" target="_blank" rel="noreferrer">Cloudflare</a>
              <span aria-hidden>•</span>
              <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">Privacy</a>
              <span aria-hidden>•</span>
              <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noreferrer">Terms</a>
            </span>
          </div>
        </Container>
      </section>

      {reviewScriptsRaw ? (
        <section
          className="footer-reviews-below bg-white text-body py-4 py-md-5 border-top"
          aria-label="Guest reviews"
        >
          <Container>
            <div className="footer-reviews-below__widgets d-flex justify-content-center">
              <ReviewWidgets reviewWidgetScripts={reviewScriptsRaw} inline />
            </div>
          </Container>
        </section>
      ) : null}
    </>
  );
};

export default Footer;

